import {useSyncExternalStore} from "react";

type ImageCacheEntry = {
    image: HTMLImageElement;
    promise: Promise<HTMLImageElement>;
    status: "loading" | "loaded" | "error";
    error?: Error;
    listeners: Set<() => void>;
};

const imageCache = new Map<string, ImageCacheEntry>();

function notify(entry: ImageCacheEntry) {
    entry.listeners.forEach(listener => listener());
}

function createEntry(url: string): ImageCacheEntry {
    const image = new window.Image();
    const entry = {
        image,
        status: "loading" as const,
        listeners: new Set<() => void>(),
    } as ImageCacheEntry;

    entry.promise = new Promise<HTMLImageElement>((resolve, reject) => {
        image.onload = async () => {
            try {
                await image.decode();
            } catch {
                // A successful load is still usable in browsers that reject decode().
            }
            entry.status = "loaded";
            notify(entry);
            resolve(image);
        };
        image.onerror = () => {
            const error = new Error(`Bild konnte nicht geladen werden: ${url}`);
            entry.status = "error";
            entry.error = error;
            notify(entry);
            reject(error);
        };
        image.src = url;
    });

    imageCache.set(url, entry);
    void entry.promise.catch(() => undefined);
    return entry;
}

function getEntry(url: string): ImageCacheEntry {
    return imageCache.get(url) ?? createEntry(url);
}

export function preloadImages(urls: readonly string[]): Promise<HTMLImageElement[]> {
    return Promise.all([...new Set(urls)].map(url => getEntry(url).promise));
}

export function evictFailedImages(urls: readonly string[]) {
    urls.forEach(url => {
        if (imageCache.get(url)?.status === "error") imageCache.delete(url);
    });
}

export function useCachedImage(url: string): HTMLImageElement | undefined {
    const entry = getEntry(url);
    return useSyncExternalStore(
        listener => {
            entry.listeners.add(listener);
            return () => entry.listeners.delete(listener);
        },
        () => entry.status === "loaded" ? entry.image : undefined,
        () => undefined,
    );
}
